import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { BaseService, PaginatedResponse } from '@tcc-mono/shared/data-access';
import {
  Animal,
  AnimalSelection,
  AnimalSelectionSource,
  AnimalSelectionStatus,
  RvoAnimal,
  SelectionMarkColor
} from '@tcc-mono/vandijk/shared/data-access/models';

type AnimalSelectionWritePayload = {
  remark?: string | null;
  source_stable_id?: number;
  destination_stable_id?: number | null;
  destination_hub_id?: number | null;
  min_birth_date?: string | null;
  max_birth_date?: string | null;
  status?: AnimalSelectionStatus;
  source?: AnimalSelectionSource;
};
import { EMPTY, Observable, expand, map, reduce } from 'rxjs';

export class AnimalSelectionAddAnimal {
  country_code: string;
  identification_number: string;
  selection_mark_color?: string | null;
  /** Bij Excel-pool: werknummer, haarkleur (code), geboortedatum voor correcte dierdata. */
  work_number?: string | null;
  haarkleur?: string | null;
  date_of_birth?: string | null;
  date_of_birth_mutation?: string | null;
}

export interface AnimalSelectionAddAnimalResponse {
  selection?: AnimalSelection;
  animal?: Animal;
  rvo_enriched?: boolean;
  rvo_error?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AnimalSelectionService extends BaseService<AnimalSelection> {
  public constructor(
    _http: HttpClient,
    @Inject(APP_CONFIG) appConfig: IAppConfig
  ) {
    super(_http, appConfig);
    this.path = 'api/animal_selections'
  }

  public override post = (body: Partial<AnimalSelection>, customUrl?: string, params: Object = {}): Observable<unknown> => {
    const payload = customUrl ? body : this.toApiPayload(body);
    const usedUrl = this.getUrl(customUrl ? `${this.path}/${customUrl}` : this.path);
    return this._http.post(usedUrl, payload, { params: Object(params) });
  };

  public override put = (id: string | number, body: Partial<AnimalSelection>, customUrl?: string): Observable<unknown> => {
    const payload = customUrl ? body : this.toApiPayload(body);
    const usedUrl = this.getUrl(`${this.path}/${id}` + (customUrl ? `/${customUrl}` : ''));
    return this._http.put(usedUrl, payload);
  };

  public postAnimal = (selectionId: number, animal: AnimalSelectionAddAnimal): Observable<AnimalSelectionAddAnimalResponse> => {
    return this.postChild<AnimalSelectionAddAnimalResponse>(animal, `${selectionId}/animals`);
  };

  public removeAnimal = (selectionId: number, animalId: number): Observable<unknown> => {
    return this.delete(selectionId, `animals/${animalId}`);
  };

  public getAnimalsPage = (
    selectionId: number,
    page = 1,
    perPage = 100
  ): Observable<PaginatedResponse<Animal>> => {
    return this._http.get<PaginatedResponse<Animal>>(
      this.getUrl(`${this.path}/${selectionId}/animals`),
      {
        params: {
          page,
          per_page: perPage,
          sort_by: 'created_at',
          sort_dir: 'desc'
        }
      }
    );
  };

  /** Haalt alle dieren op (alle pagina's). */
  public getAllAnimals = (selectionId: number): Observable<Animal[]> => {
    return this.getAnimalsPage(selectionId, 1, 100).pipe(
      expand((response) => {
        const lastPage = response.meta?.last_page ?? 1;
        const currentPage = response.meta?.current_page ?? 1;
        if (currentPage >= lastPage) {
          return EMPTY;
        }
        return this.getAnimalsPage(selectionId, currentPage + 1, 100);
      }),
      map((response) => response.data ?? []),
      reduce((acc, pageAnimals) => [...acc, ...pageAnimals], [] as Animal[])
    );
  };

  /** Filterkleuren met min/max leeftijd voor deze selectie (per-selectie instellingen). */
  public getSelectionMarkColorsForSelection = (selectionId: number): Observable<SelectionMarkColor[]> => {
    return this._http.get<SelectionMarkColor[]>(this.getUrl(`${this.path}/${selectionId}/selection_mark_colors`));
  };

  /** Filterinstellingen bijwerken: min/max per kleur voor deze selectie. */
  public updateSelectionMarkColors = (
    selectionId: number,
    body: { selection_mark_colors: Array<{ color_name: string; used?: boolean; min_birth_date?: string | null; max_birth_date?: string | null }> }
  ): Observable<SelectionMarkColor[]> => {
    return this._http.patch<SelectionMarkColor[]>(
      this.getUrl(`${this.path}/${selectionId}/selection_mark_colors`),
      body
    );
  };

  public updateAnimalMarkColor = (
    selectionId: number,
    animalId: number,
    selection_mark_color: string | null
  ): Observable<unknown> => {
    return this._http.patch<unknown>(
      this.getUrl(`${this.path}/${selectionId}/animals/${animalId}`),
      { selection_mark_color }
    );
  };

  public mergeSelections = (selectionIds: number[]): Observable<unknown> => {
    const postBody = {
      selections: [...selectionIds]
    };

    return this.post(postBody, '/merge');
  };

  public createPassports = (selectionId: number): Observable<unknown> => {
    return this.postChild(null, `${selectionId}/create_passports`);
  };

  public export = (selectionId: number, body: { date: string }): Observable<unknown> => {
    return this.postChild(body, `${selectionId}/export`);
  };

  public exportFromHub = (selectionId: number, body: { date: string, hub_id: number }): Observable<unknown> => {
    return this.postChild(body, `${selectionId}/export-from-hub`);
  };

  public transport = (selectionId: number, body: { date: string, stable_id: number }): Observable<unknown> => {
    return this.postChild(body, `${selectionId}/transport`);
  };

  getAnimalsExcel = (selectionId: number) => {
    return this._http.get(
      `${this.getUrl(
        `${this.path}/${selectionId}/export_animals`
      )}`,
      {
        responseType: 'blob'
      }
    );
  };

  getAnimalsExcelByColor = (selectionId: number) => {
    return this._http.get(
      `${this.getUrl(
        `${this.path}/${selectionId}/export_animals_by_color`
      )}`,
      {
        responseType: 'blob'
      }
    );
  };

  getAnimalsPdf = (selectionId: number) => {
    return this._http.get(
      `${this.getUrl(
        `${this.path}/${selectionId}/export_animals_pdf`
      )}`,
      {
        responseType: 'blob',
        observe: 'response',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );
  };

  /** Lijst van Excel-dieren voor selectie (bij source = excel). */
  getExcelAnimals = (selectionId: number): Observable<RvoAnimal[]> =>
    this._http.get<RvoAnimal[]>(this.getUrl(`${this.path}/${selectionId}/excel_animals`));

  /** Download template voor Excel-dieren upload. */
  getExcelAnimalsTemplate = (): Observable<Blob> =>
    this._http.get(this.getUrl(`${this.path}/excel_animals_template`), { responseType: 'blob' });

  /** Excel uploaden: vervangt excel-dieren voor deze selectie. */
  uploadExcelAnimals = (selectionId: number, file: File): Observable<RvoAnimal[]> => {
    const formData = new FormData();
    formData.append('file', file);
    return this._http.post<RvoAnimal[]>(
      this.getUrl(`${this.path}/${selectionId}/excel_animals`),
      formData
    );
  };

  private toApiPayload(body: Partial<AnimalSelection>): AnimalSelectionWritePayload {
    const payload: AnimalSelectionWritePayload = {};

    const remark = body.remark ?? body.name;
    if (remark !== undefined) {
      payload.remark = remark?.toString().trim() || null;
    }

    if (body.source_stable_id !== undefined) {
      payload.source_stable_id = body.source_stable_id;
    }
    if (body.destination_stable_id !== undefined) {
      payload.destination_stable_id = body.destination_stable_id;
    }
    if (body.destination_hub_id !== undefined) {
      payload.destination_hub_id = body.destination_hub_id;
    }
    if (body.min_birth_date !== undefined) {
      payload.min_birth_date = body.min_birth_date;
    }
    if (body.max_birth_date !== undefined) {
      payload.max_birth_date = body.max_birth_date;
    }
    if (body.status !== undefined) {
      payload.status = body.status;
    }
    if (body.source !== undefined) {
      payload.source = body.source;
    } else if (body.use_rvo !== undefined) {
      payload.source = (body.use_rvo ? 'rvo' : 'excel') as AnimalSelectionSource;
    } else {
      payload.source = 'rvo';
    }

    return payload;
  }
}
